import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from 'react';

import { getLandingContent } from '../auth/landing/landingContent';
import { businessLanguageFromPath, businessPath, getBusinessContent } from '../business/businessContent';

export type SupportedLanguage =
  | 'ko'
  | 'en'
  | 'ja'
  | 'zh'
  | 'es'
  | 'pt'
  | 'de'
  | 'fr'
  | 'id'
  | 'hi';
export type PublicKnowledgeLanguage = 'ko' | 'en' | 'ja';
export type DemoLanguage = 'ko' | 'en';
export type AppCopyLanguage = PublicKnowledgeLanguage;

export type LocalizedValue<T> =
  Record<PublicKnowledgeLanguage, T> & Partial<Record<SupportedLanguage, T>>;

type LocalizedMap = Readonly<Record<PublicKnowledgeLanguage, unknown>> &
  Readonly<Partial<Record<SupportedLanguage, unknown>>>;
type LocalizedReturn<TValues extends LocalizedMap> =
  TValues extends Readonly<Record<PublicKnowledgeLanguage, infer TValue>>
    ? TValue
    : never;

export const DEFAULT_LANGUAGE: SupportedLanguage = 'ko';
const STORAGE_KEY = 'jeeum.language';

export const SUPPORTED_LANGUAGES = [
  'ko',
  'en',
  'ja',
  'zh',
  'es',
  'pt',
  'de',
  'fr',
  'id',
  'hi',
] as const;

export const PUBLIC_KNOWLEDGE_LANGUAGES = ['ko', 'en', 'ja'] as const;
export const DEMO_LANGUAGES = ['ko', 'en'] as const;

export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  ko: '한국어',
  en: 'English',
  ja: '日本語',
  zh: '中文',
  es: 'Español',
  pt: 'Português',
  de: 'Deutsch',
  fr: 'Français',
  id: 'Bahasa Indonesia',
  hi: 'हिन्दी',
};

const INTL_LOCALES: Record<SupportedLanguage, string> = {
  ko: 'ko-KR',
  en: 'en-US',
  ja: 'ja-JP',
  zh: 'zh-CN',
  es: 'es-ES',
  pt: 'pt-BR',
  de: 'de-DE',
  fr: 'fr-FR',
  id: 'id-ID',
  hi: 'hi-IN',
};

interface LanguageContextValue {
  language: SupportedLanguage;
  setLanguage: (language: SupportedLanguage) => void;
  setLanguageFromRoute: (language: SupportedLanguage) => void;
}

const LanguageContext = createContext<LanguageContextValue>({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => undefined,
  setLanguageFromRoute: () => undefined,
});

export function isSupportedLanguage(
  value: string | null | undefined,
): value is SupportedLanguage {
  return SUPPORTED_LANGUAGES.includes(value as SupportedLanguage);
}

export function isPublicKnowledgeLanguage(
  value: string | null | undefined,
): value is PublicKnowledgeLanguage {
  return PUBLIC_KNOWLEDGE_LANGUAGES.includes(value as PublicKnowledgeLanguage);
}

export function isDemoLanguage(
  value: string | null | undefined,
): value is DemoLanguage {
  return DEMO_LANGUAGES.includes(value as DemoLanguage);
}

export function resolveAppCopyLanguage(language: SupportedLanguage): AppCopyLanguage {
  return isPublicKnowledgeLanguage(language) ? language : 'en';
}

function readStoredLanguage(): SupportedLanguage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const stored = window.localStorage.getItem(STORAGE_KEY);
  return isSupportedLanguage(stored) ? stored : null;
}

function readNavigatorLanguage(): SupportedLanguage | null {
  if (typeof navigator === 'undefined') {
    return null;
  }

  const candidates: string[] = [];
  if (Array.isArray(navigator.languages)) {
    candidates.push(...navigator.languages);
  }
  if (typeof navigator.language === 'string') {
    candidates.push(navigator.language);
  }

  for (const candidate of candidates) {
    if (typeof candidate !== 'string') {
      continue;
    }
    const primary = candidate.toLowerCase().split(/[-_]/)[0];
    if (isSupportedLanguage(primary)) {
      return primary;
    }
  }
  return null;
}

/**
 * URL 기반 SSR 과 첫 client render 의 locale 결정 정책.
 * 우선순위:
 *   1) exact `/` 는 `ko`, business canonical 경로는 해당 언어로 서버 계약과 일치
 *   2) URL prefix (`/en/...`, `/ja/...`)
 *   3) localStorage `jeeum.language` (UI 픽커가 명시적으로 저장한 값)
 *   4) 지원되는 `navigator.language`
 *   5) `ko`
 *
 * `/demo` 의 canonical 언어 고정은 App 의 LanguageRouteSync 가 담당한다.
 */
export function resolveDefaultLanguage(pathname: string | null | undefined): SupportedLanguage {
  if (pathname === '/') {
    return DEFAULT_LANGUAGE;
  }
  const businessLanguage = businessLanguageFromPath(pathname);
  if (businessLanguage) {
    return businessLanguage;
  }
  const fromPath = pathname ? readLanguageFromPathname(pathname) : null;
  if (fromPath) {
    return fromPath;
  }
  const fromStorage = readStoredLanguage();
  if (fromStorage) {
    return fromStorage;
  }
  const fromNavigator = readNavigatorLanguage();
  if (fromNavigator) {
    return fromNavigator;
  }
  return DEFAULT_LANGUAGE;
}

function normalizePathname(pathname: string): string {
  if (!pathname) {
    return '/';
  }

  if (pathname === '/') {
    return pathname;
  }

  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed.length > 0 ? trimmed : '/';
}

export function readLanguageFromPathname(pathname: string | null | undefined): SupportedLanguage | null {
  if (!pathname) {
    return null;
  }

  const [, firstSegment] = pathname.split('/');
  return isSupportedLanguage(firstSegment) ? firstSegment : null;
}

export function stripLanguageFromPathname(pathname: string): string {
  const normalized = normalizePathname(pathname);
  const routeLanguage = readLanguageFromPathname(normalized);
  if (!routeLanguage) {
    return normalized;
  }

  const stripped = normalized.slice(routeLanguage.length + 1);
  return stripped.length > 0 ? stripped : '/';
}

function isSingleSlugPath(pathname: string, prefix: string): boolean {
  if (!pathname.startsWith(prefix)) {
    return false;
  }
  const slug = pathname.slice(prefix.length);
  return slug.length > 0 && !slug.includes('/');
}

export function isPublicPracticeSongPath(pathname: string): boolean {
  const normalized = normalizePathname(pathname);
  return (
    readLanguageFromPathname(normalized) === 'ko'
    && isSingleSlugPath(
      normalizePathname(stripLanguageFromPathname(normalized)),
      '/practice-songs/',
    )
  );
}

export function isPublicPracticeCatalogPath(pathname: string): boolean {
  const normalized = normalizePathname(pathname);
  return (
    readLanguageFromPathname(normalized) === 'ko'
    && normalizePathname(stripLanguageFromPathname(normalized))
      === '/practice-songs'
  );
}

export function isPublicPracticeArtistPath(pathname: string): boolean {
  const normalized = normalizePathname(pathname);
  return (
    readLanguageFromPathname(normalized) === 'ko'
    && isSingleSlugPath(
      normalizePathname(stripLanguageFromPathname(normalized)),
      '/artists/',
    )
  );
}

export function isPublicPracticeRoomPath(pathname: string): boolean {
  const normalized = normalizePathname(pathname);
  return (
    readLanguageFromPathname(normalized) === 'ko'
    && isSingleSlugPath(
      normalizePathname(stripLanguageFromPathname(normalized)),
      '/practice/',
    )
  );
}

export function isLocaleAwarePublicPath(pathname: string): boolean {
  const normalized = normalizePathname(pathname);
  if (
    normalized === '/' ||
    normalized === '/login' ||
    normalized.startsWith('/share/')
  ) {
    return true;
  }

  const routeLanguage = readLanguageFromPathname(normalized);
  const strippedPath = routeLanguage
    ? normalizePathname(stripLanguageFromPathname(normalized))
    : normalized;
  if (routeLanguage && strippedPath === '/') {
    return true;
  }
  if (
    isPublicPracticeCatalogPath(normalized)
    || isPublicPracticeArtistPath(normalized)
    || isPublicPracticeSongPath(normalized)
    || isPublicPracticeRoomPath(normalized)
  ) {
    return true;
  }
  if (
    strippedPath === '/use-cases' ||
    strippedPath.startsWith('/use-cases/') ||
    strippedPath.startsWith('/guides/') ||
    strippedPath.startsWith('/compare/')
  ) {
    return routeLanguage ? isPublicKnowledgeLanguage(routeLanguage) : true;
  }
  // 블로그는 랜딩이 지원하는 10개 언어 전체에서 노출된다.
  if (strippedPath === '/blog' || strippedPath.startsWith('/blog/')) {
    return true;
  }
  return (
    strippedPath === '/'
  );
}

export function localizePublicPath(language: SupportedLanguage, pathname: string): string {
  const normalized = normalizePathname(pathname);
  if (businessLanguageFromPath(normalized)) {
    return businessPath(language === 'ko' ? 'ko' : 'en');
  }
  if (normalized === '/demo') {
    return language === 'ko' ? '/demo' : '/en/demo';
  }
  if (
    normalized === '/login' ||
    normalized.startsWith('/share/')
  ) {
    return normalized;
  }
  if (!isLocaleAwarePublicPath(normalized)) {
    return pathname;
  }

  const strippedPath = normalizePathname(stripLanguageFromPathname(normalized));
  if (
    strippedPath === '/practice-songs'
    || isSingleSlugPath(strippedPath, '/artists/')
    || isSingleSlugPath(strippedPath, '/practice-songs/')
    || isSingleSlugPath(strippedPath, '/practice/')
  ) {
    return language === 'ko' ? `/ko${strippedPath}` : `/${language}`;
  }
  if (
    (
      strippedPath === '/use-cases' ||
      strippedPath.startsWith('/use-cases/') ||
      strippedPath.startsWith('/guides/') ||
      strippedPath.startsWith('/compare/')
    ) &&
    !isPublicKnowledgeLanguage(language)
  ) {
    return `/${language}`;
  }
  return strippedPath === '/' ? `/${language}` : `/${language}${strippedPath}`;
}

function readInitialLanguage(): SupportedLanguage {
  if (typeof window === 'undefined') {
    return DEFAULT_LANGUAGE;
  }

  return resolveDefaultLanguage(window.location.pathname);
}

export function pickLocalized<TValues extends LocalizedMap>(
  language: SupportedLanguage,
  values: TValues,
): LocalizedReturn<TValues> {
  return (values[language] ?? values.en ?? values.ko) as LocalizedReturn<TValues>;
}

export function getBreadcrumbLabel(language: SupportedLanguage): string {
  return getLandingContent(language).nav.breadcrumbLabel;
}

export function getIntlLocale(language: SupportedLanguage): string {
  return INTL_LOCALES[language];
}

export function LanguageProvider({ children }: PropsWithChildren) {
  const [language, setLanguageState] = useState<SupportedLanguage>(() => readInitialLanguage());

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    document.documentElement.lang = language;
    document.documentElement.setAttribute('data-language', language);
  }, [language]);

  // 명시적 사용자 선택 (UI 픽커) — localStorage 에 영구 저장.
  const setLanguage = (next: SupportedLanguage) => {
    persistLanguagePreference(next);
    setLanguageState(next);
  };

  // URL prefix 변경에 의한 라우트 기반 동기화 — localStorage 에 저장하지 않는다.
  // (직전 라우트 언어가 prefix 없는 라우트로 따라가는 sticky 동작을 막기 위해.)
  const setLanguageFromRoute = (next: SupportedLanguage) => {
    setLanguageState(next);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, setLanguageFromRoute }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function persistLanguagePreference(language: SupportedLanguage) {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, language);
  }
}

export function useLanguage() {
  return useContext(LanguageContext);
}

const STEM_LABELS: Record<string, LocalizedValue<string>> = {
  vocals: { ko: '보컬', en: 'Vocals', ja: 'ボーカル' },
  drums: { ko: '드럼', en: 'Drums', ja: 'ドラム' },
  bass: { ko: '베이스', en: 'Bass', ja: 'ベース' },
  piano: { ko: '피아노', en: 'Piano', ja: 'ピアノ' },
  guitar: { ko: '기타', en: 'Guitar', ja: 'ギター' },
  other: { ko: '기타 외', en: 'Other', ja: 'その他' },
  lead_guitar: { ko: '리드 기타', en: 'Lead Guitar', ja: 'リードギター' },
  rhythm_guitar: { ko: '리듬 기타', en: 'Rhythm Guitar', ja: 'リズムギター' },
  electric_guitar: { ko: '일렉 기타', en: 'Electric Guitar', ja: 'エレキギター' },
  acoustic_guitar: { ko: '어쿠스틱 기타', en: 'Acoustic Guitar', ja: 'アコースティックギター' },
  uploaded_score: { ko: '업로드 악보', en: 'Uploaded Score', ja: 'アップロード譜面' },
  metronome: { ko: '메트로놈', en: 'Metronome', ja: 'メトロノーム' },
};

export function getStemLabel(
  language: SupportedLanguage,
  stemName: string,
  fallback?: string | null,
): string {
  const known = STEM_LABELS[stemName];
  return known ? pickLocalized(language, known) : (fallback ?? stemName);
}

const SEPARATION_PHASE_LABELS: Record<string, LocalizedValue<string>> = {
  queued: {
    ko: '대기 중…',
    en: 'Queued…',
    ja: '待機中…',
  },
  download_source: {
    ko: '원본 다운로드 중…',
    en: 'Downloading source…',
    ja: '元音源をダウンロード中…',
  },
  extract_audio: {
    ko: '오디오 추출 중…',
    en: 'Extracting audio…',
    ja: '音声を抽出中…',
  },
  separate_main: {
    ko: '트랙 분리 중…',
    en: 'Separating tracks…',
    ja: 'トラックを分離中…',
  },
  guitar_split: {
    ko: '기타 세부 분리 중…',
    en: 'Refining guitar stems…',
    ja: 'ギターステムを細分化中…',
  },
  extract_metadata: {
    ko: '메타데이터 추출 중…',
    en: 'Extracting metadata…',
    ja: 'メタデータを抽出中…',
  },
  compute_waveform: {
    ko: '파형 생성 중…',
    en: 'Building waveform…',
    ja: '波形を生成中…',
  },
  prepare_studio_audio: {
    ko: '스튜디오 오디오 준비 중…',
    en: 'Preparing studio audio…',
    ja: 'スタジオ音声を準備中…',
  },
  prepare_analysis_cache: {
    ko: '분석 캐시 준비 중…',
    en: 'Preparing analysis cache…',
    ja: '解析キャッシュを準備中…',
  },
  completed: {
    ko: '완료',
    en: 'Completed',
    ja: '完了',
  },
  failed: {
    ko: '실패',
    en: 'Failed',
    ja: '失敗',
  },
};

export function getSeparationPhaseLabel(
  language: SupportedLanguage,
  phase: string | null | undefined,
  fallback: string,
): string {
  if (!phase) {
    return fallback;
  }

  const label = SEPARATION_PHASE_LABELS[phase];
  return label ? pickLocalized(language, label) : fallback;
}

// ---- Document title (route key) ----------------------------------------

export type RouteKey =
  | 'landing'
  | 'business'
  | 'login'
  | 'demo'
  | 'share'
  | 'studio.library'
  | 'studio.billing'
  | 'studio.detail'
  | 'guitar-play'
  | 'admin'
  | 'public.useCases'
  | 'public.useCase'
  | 'public.guide'
  | 'public.compare'
  | 'public.practiceCatalog'
  | 'public.practiceArtist'
  | 'public.practiceSong'
  | 'blog.index'
  | 'blog.article'
  | 'not-found';

const APP_BRAND = '지음';
const APP_BRAND_EN = 'Jeeum';

const ROUTE_TITLES: Record<Exclude<RouteKey, 'landing' | 'business'>, LocalizedValue<string>> = {
  login: {
    ko: `로그인 · ${APP_BRAND}`,
    en: `Sign in · ${APP_BRAND_EN}`,
    ja: `ログイン · ${APP_BRAND}`,
  },
  demo: {
    ko: `데모 스튜디오 · ${APP_BRAND}`,
    en: `Demo Studio · ${APP_BRAND_EN}`,
    ja: `デモスタジオ · ${APP_BRAND}`,
  },
  share: {
    ko: `공유 스튜디오 · ${APP_BRAND}`,
    en: `Shared Studio · ${APP_BRAND_EN}`,
    ja: `共有スタジオ · ${APP_BRAND}`,
  },
  'studio.library': {
    ko: `스튜디오 · ${APP_BRAND}`,
    en: `Studio · ${APP_BRAND_EN}`,
    ja: `スタジオ · ${APP_BRAND}`,
  },
  'studio.billing': {
    ko: `크레딧 충전 · ${APP_BRAND}`,
    en: `Top up credits · ${APP_BRAND_EN}`,
    ja: `クレジット追加 · ${APP_BRAND}`,
  },
  'studio.detail': {
    ko: `스튜디오 룸 · ${APP_BRAND}`,
    en: `Studio Room · ${APP_BRAND_EN}`,
    ja: `スタジオルーム · ${APP_BRAND}`,
  },
  'guitar-play': {
    ko: `기타 플레이 · ${APP_BRAND}`,
    en: `Guitar Play · ${APP_BRAND_EN}`,
    ja: `ギタープレイ · ${APP_BRAND}`,
  },
  admin: {
    ko: `관리자 · ${APP_BRAND}`,
    en: `Admin · ${APP_BRAND_EN}`,
    ja: `管理者 · ${APP_BRAND}`,
  },
  'public.useCases': {
    ko: `공개 가이드 · ${APP_BRAND}`,
    en: `Public Guides · ${APP_BRAND_EN}`,
    ja: `公開ガイド · ${APP_BRAND}`,
  },
  'public.useCase': {
    ko: `사용 사례 · ${APP_BRAND}`,
    en: `Use Case · ${APP_BRAND_EN}`,
    ja: `ユースケース · ${APP_BRAND}`,
  },
  'public.guide': {
    ko: `가이드 · ${APP_BRAND}`,
    en: `Guide · ${APP_BRAND_EN}`,
    ja: `ガイド · ${APP_BRAND}`,
  },
  'public.compare': {
    ko: `비교 · ${APP_BRAND}`,
    en: `Compare · ${APP_BRAND_EN}`,
    ja: `比較 · ${APP_BRAND}`,
  },
  'public.practiceCatalog': {
    ko: `공개 보컬 연습곡 · ${APP_BRAND}`,
    en: `Public Vocal Practice Songs · ${APP_BRAND_EN}`,
    ja: `公開ボーカル練習曲 · ${APP_BRAND}`,
  },
  'public.practiceArtist': {
    ko: `아티스트 보컬 연습곡 · ${APP_BRAND}`,
    en: `Artist Vocal Practice Songs · ${APP_BRAND_EN}`,
    ja: `アーティスト別ボーカル練習曲 · ${APP_BRAND}`,
  },
  'public.practiceSong': {
    ko: `공개 연습곡 · ${APP_BRAND}`,
    en: `Public Practice Song · ${APP_BRAND_EN}`,
    ja: `公開練習曲 · ${APP_BRAND}`,
  },
  'blog.index': {
    ko: `블로그 · ${APP_BRAND}`,
    en: `Blog · ${APP_BRAND_EN}`,
    ja: `ブログ · ${APP_BRAND}`,
  },
  'blog.article': {
    ko: `블로그 · ${APP_BRAND}`,
    en: `Blog · ${APP_BRAND_EN}`,
    ja: `ブログ · ${APP_BRAND}`,
  },
  'not-found': {
    ko: `페이지를 찾을 수 없습니다 · ${APP_BRAND}`,
    en: `Page not found · ${APP_BRAND_EN}`,
    ja: `ページが見つかりません · ${APP_BRAND}`,
  },
};

/**
 * pathname 을 RouteKey 로 매핑한다. locale prefix 는 무시한다.
 * 알 수 없는 경로는 'not-found' 로 떨어진다.
 */
export function resolveRouteKey(pathname: string | null | undefined): RouteKey {
  if (!pathname) {
    return 'not-found';
  }

  const normalized = normalizePathname(pathname);
  const routeLanguage = readLanguageFromPathname(normalized);
  const stripped = routeLanguage
    ? normalizePathname(stripLanguageFromPathname(normalized))
    : normalized;

  if (
    routeLanguage &&
    (stripped === '/login' || stripped.startsWith('/share/'))
  ) {
    return 'not-found';
  }

  if (stripped === '/' || stripped === '') {
    return 'landing';
  }
  if (stripped === '/login') {
    return 'login';
  }
  if (stripped === '/business') {
    return businessLanguageFromPath(normalized) ? 'business' : 'not-found';
  }
  if (stripped === '/demo') {
    return !routeLanguage || routeLanguage === 'en' ? 'demo' : 'not-found';
  }
  if (stripped.startsWith('/share/')) {
    return 'share';
  }
  if (stripped === '/use-cases') {
    return !routeLanguage || isPublicKnowledgeLanguage(routeLanguage)
      ? 'public.useCases'
      : 'not-found';
  }
  if (stripped.startsWith('/use-cases/')) {
    return !routeLanguage || isPublicKnowledgeLanguage(routeLanguage)
      ? 'public.useCase'
      : 'not-found';
  }
  if (stripped.startsWith('/guides/')) {
    return !routeLanguage || isPublicKnowledgeLanguage(routeLanguage)
      ? 'public.guide'
      : 'not-found';
  }
  if (stripped.startsWith('/compare/')) {
    return !routeLanguage || isPublicKnowledgeLanguage(routeLanguage)
      ? 'public.compare'
      : 'not-found';
  }
  if (stripped === '/practice-songs') {
    return routeLanguage === 'ko'
      ? 'public.practiceCatalog'
      : 'not-found';
  }
  if (isSingleSlugPath(stripped, '/artists/')) {
    return routeLanguage === 'ko'
      ? 'public.practiceArtist'
      : 'not-found';
  }
  if (isSingleSlugPath(stripped, '/practice-songs/')) {
    return routeLanguage === 'ko' ? 'public.practiceSong' : 'not-found';
  }
  if (stripped === '/blog') {
    return 'blog.index';
  }
  if (stripped.startsWith('/blog/')) {
    return 'blog.article';
  }
  if (stripped === '/studio') {
    return 'studio.library';
  }
  if (stripped === '/studio/billing') {
    return 'studio.billing';
  }
  if (stripped === '/studio/credits' || stripped.startsWith('/studio/credits/')) {
    return 'not-found';
  }
  if (stripped === '/studio/credit-requests' || stripped.startsWith('/studio/credit-requests/')) {
    return 'not-found';
  }
  if (stripped.startsWith('/studio/')) {
    return 'studio.detail';
  }
  if (stripped === '/guitar-play' || stripped.startsWith('/guitar-play/')) {
    return 'guitar-play';
  }
  if (stripped === '/admin' || stripped.startsWith('/admin/')) {
    return 'admin';
  }

  return 'not-found';
}

export function getRouteTitle(language: SupportedLanguage, routeKey: RouteKey): string {
  if (routeKey === 'landing') {
    return getLandingContent(language).meta.title;
  }
  if (routeKey === 'business') {
    return getBusinessContent(language === 'ko' ? 'ko' : 'en').meta.title;
  }
  return pickLocalized(language, ROUTE_TITLES[routeKey]);
}

/**
 * 현재 라우트 + 언어에 맞는 `document.title` 을 동기화한다.
 * - routeKey 를 명시하면 그 키를, 아니면 location.pathname 으로 자동 매핑.
 * - 라우트/언어 변경 모두에 반응한다.
 */
export function useDocumentTitle(routeKey?: RouteKey): string {
  const { language } = useLanguage();
  // location 을 직접 import 하지 않는 이유: 이 모듈은 react-router 의존성이 없음.
  // 기본 매핑은 호출부에서 `routeKey` 또는 `useDocumentTitleFromPath` 로 결정.
  const resolvedKey: RouteKey = routeKey ?? 'not-found';
  const title = useMemo(() => getRouteTitle(language, resolvedKey), [language, resolvedKey]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    document.title = title;
  }, [title]);

  return title;
}

/**
 * pathname 을 받아 RouteKey 를 자동 매핑한 뒤 document.title 을 동기화한다.
 * App.tsx 의 `LanguageRouteSync` 옆에 한 번만 마운트하면 라우트 + 언어 변경 모두 반응.
 *
 * 언어 결정: business canonical 경로 또는 locale prefix가 있으면 그것이 source of truth
 * (LanguageProvider의 context 언어보다 우선). 그 외에는 context 언어를 사용 — UI 픽커가 영구 저장한 값
 * 또는 navigator/storage 기반의 default 가 그대로 적용된다.
 */
export function useDocumentTitleFromPath(pathname: string, accountBrand?: string): string {
  const { language: contextLanguage } = useLanguage();
  const language = useMemo<SupportedLanguage>(() => {
    const fromPath = businessLanguageFromPath(pathname) ?? readLanguageFromPathname(pathname);
    return fromPath ?? contextLanguage;
  }, [pathname, contextLanguage]);
  const routeKey = useMemo(() => resolveRouteKey(pathname), [pathname]);
  const title = useMemo(() => {
    const defaultTitle = getRouteTitle(language, routeKey);
    return accountBrand ? defaultTitle.replace(/(?:지음|Jeeum)$/, accountBrand) : defaultTitle;
  }, [language, routeKey, accountBrand]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    // Blog routes own their catalog title; public links load the matching SSR head.
    if (routeKey === 'blog.index' || routeKey === 'blog.article') {
      return;
    }
    document.title = title;
  }, [title, routeKey]);

  return title;
}
